import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import produce from 'immer'
import { Domains } from '@ally/data-types-host'
import { mergeDeepRight } from 'ramda'
import { CustomizationOptions } from '@ally/data-types-customization'
import { LoadState } from '@ally/use-data-domain'
import { useAccountTypes } from '../../providers'
import { useHostServices } from '../../HostServices'
import { DashboardView } from '../types'
import { getFormattedData, getUpdatedCustomizeDataObj } from '../utils'
import customizeDashboardImmerReducer, {
  CustomizeState,
  CustomizeDispatch,
} from './CustomizeDashboardReducer'
import {
  getEligibleWidgets,
  getDashboardView,
  getWidgets,
  hasTransferAccounts,
  NEVER_SAVED_CUSTOMIZATIONS,
  saveCustomizationsUtil,
  saveDashboardView,
} from './utils'
import { useBreakdownEligibility } from '../../hooks'
import { getRoles } from '../../utils'
import { useUpdateCustomizationData } from '../hooks/useUpdateCustomizationData'
import { widgetsConfig } from '../widgetsConfig'

export const defaultCustomizeApiAlert = {
  shouldRender: false,
  type: '',
  isDashboardError: false,
}

type AdditionalFunctions = {
  saveCustomizations: () => void
  setDashboardView: (view: DashboardView) => void
}

export type CustomizeDashboardStateAndFunctions = CustomizeState &
  AdditionalFunctions

export type CustomizeDashboardContextValue = [
  CustomizeDashboardStateAndFunctions,
  CustomizeDispatch,
]

export const CustomizeDashboardContext = createContext<
  CustomizeDashboardContextValue | undefined
>(undefined)

export const CustomizeDashboardProvider: React.FC = ({ children }) => {
  const { hostData, featureFlags, session } = useHostServices()
  const {
    activeDepositAccounts,
    hasAtLeastOneActiveAccount,
    hasOpenSdaAccounts,
    investAccounts,
    isBankOutage,
    isInvestOutage,
    isMortgageUser,
    isWealthFullOutage,
    isWealthProspect,
    openDepositAccounts,
    stkSummary,
    isDebtPlanUser,
    isCCUser,
    allCCAccounts,
    allAutoAccounts,
  } = useAccountTypes()

  const firstRender = useRef(true)

  const roles = getRoles(session)
  const isAutoOnlyUser = roles.isOnly('auto')

  const defaultCustomizationData = getFormattedData({
    activeDepositAccounts: activeDepositAccounts ?? [],
    investAccounts,
    isBankOutage,
    isInvestOutage,
    isWealthFullOutage,
    isWealthProspect,
    isMortgageCustomer: isMortgageUser,
    isAutoCustomer:
      featureFlags.variation('FF_SPOG_WEB_UI', true) && roles.auto,
    isDebtPlanUser,
    isCCUser,
    creditCardAccounts: allCCAccounts,
    autoAccounts: allAutoAccounts,
  })

  const hostDataCustomization = hostData[Domains.CUSTOMIZATION]

  const customizeResp = hostDataCustomization.data?.find(
    ({ channel }) => channel === 'WEB',
  )

  const customizeRespError = hostDataCustomization.status.loadState === 'failed'

  const isCustomizationEnabled =
    featureFlags.variation('FF_snapshot-customization', true) &&
    !customizeRespError &&
    !isAutoOnlyUser

  const hasBreakdownWidgetAdded = !!customizeResp?.widgets.find(custWidget => {
    return custWidget.name === 'accountsOverviewWidget' && custWidget.enabled
  })

  const showBreakdown = useBreakdownEligibility(hasBreakdownWidgetAdded)

  const showWeeklyFinancialInsights =
    hasAtLeastOneActiveAccount &&
    featureFlags.variation('FF_invest-widget', true)

  const showBadgeWidget = featureFlags.variation('FF_badge-widget', true)

  /**
   * Eligible widgets are all widgets available to the user
   * that meet eligibility requirements.
   * i.e. for the stk widget, user has a bucket or booster and
   * the widget FF is enabled. This property is used to determine
   * which widgets a user can see and overrides any previous
   * customization settings which may no longer be valid.
   */
  const eligibleWidgets = useMemo(() => {
    return getEligibleWidgets(
      widgetsConfig({
        stkSummary,
        hasTransferAccounts: hasTransferAccounts(openDepositAccounts),
        hasOpenSdaAccounts,
        showBreakdown,
        showWeeklyFinancialInsights,
        showBadgeWidget,
      }),
    )
  }, [
    stkSummary,
    hasOpenSdaAccounts,
    showBreakdown,
    showWeeklyFinancialInsights,
    openDepositAccounts,
    showBadgeWidget,
  ])

  const customizeApiData = customizeResp?.customizationId
    ? getUpdatedCustomizeDataObj({
        defaultCustomizations: defaultCustomizationData,
        customizationResponse: customizeResp,
        eligibleWidgets,
        isDebtPlanUser,
      })
    : defaultCustomizationData

  const widgetsFromApi =
    // this exists because the old API didn't allow for an empty array to be saved, we can probably only remove this if the database is purged of this fake widget
    customizeResp?.widgets[0]?.name === 'none' ? [] : customizeApiData.widgets

  const widgets = useMemo(
    () =>
      getWidgets({
        eligibleWidgets,
        widgetsFromApi,
      }),
    [eligibleWidgets, widgetsFromApi],
  )

  //  if it is the user's first time ever saving customizations AND they use the view toggle, we need to assign the widgets since the old behavior would send an empty widget array
  if (firstRender.current && !customizeResp?.customizationId) {
    firstRender.current = false
    customizeApiData.widgets = widgets
  }

  function determineDashboardView(): DashboardView {
    // if widgets are available we can check the customization resp for the user's customized view
    return isCustomizationEnabled &&
      Object.keys(eligibleWidgets).length > 0 &&
      widgets.some(w => w.enabled)
      ? getDashboardView(customizeResp)
      : DashboardView.DEFAULT
  }

  const initialState: CustomizeState = {
    // updates when saveCustomizations is successful
    customizeApiData,
    customizeApiAlert: defaultCustomizeApiAlert,
    dashboardView: determineDashboardView(),
    isCustomizeModalOpen: false,
    isCustomizationEnabled,
    // updates when saveCustomizations is successful
    widgets, // current widgets added
    eligibleWidgets, // all possible widgets to add
    customizeDataToPatch: {},
    hasCustomizeChangesToSave: false,
    isSaving: false,
  }

  const [state, customizeDispatch] = useReducer(
    // immer
    produce(customizeDashboardImmerReducer),
    initialState,
  )

  // Ensures the customized snapshot is updated when the authenticated FF's are returned, useful for pilot segments
  useEffect(() => {
    const authFlagsLoaded = featureFlags.loadState === LoadState.LOADED
    if (authFlagsLoaded) {
      customizeDispatch({
        type: 'set customizeApiData',
        payload: customizeApiData,
      })

      customizeDispatch({
        type: 'set widgets',
        payload: widgets,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.loadState])

  useEffect(() => {
    // 1. re-render widgets if accounts data is refreshed and there is change in widgets
    // 2. Send dispatch only if customization data is in loaded state,
    // else it would cause a flash of state data if customization data is being updated
    if (hostDataCustomization.status.loadState === LoadState.LOADED) {
      customizeDispatch({
        type: 'set widgets',
        payload: widgets,
      })
    }
    // adding widgets as dependency causes infinite re-rendering, thus run useEffect when eligibleWidgets are updated
  }, [eligibleWidgets, hostDataCustomization.status.loadState])

  useEffect(() => {
    if (customizeRespError) {
      customizeDispatch({
        type: 'CustomizeDashboard API alert',
        payload: {
          shouldRender: true,
          type: 'error',
          isDashboardError: true,
        },
      })
    }
  }, [customizeRespError])

  // The LD call always fires twice. So the 1st call will return the default values for FF's.
  // Then the 2nd network call will include the current user and return the appropriate values for that user.
  // Ex: If the 1st call is true for customization and the 2nd one is false we need to update that value in our reducer state
  useEffect(() => {
    // this if statement skips the first render useEffect call
    if (state.isCustomizationEnabled !== isCustomizationEnabled) {
      customizeDispatch({
        type: 'set isCustomizationEnabled',
        payload: isCustomizationEnabled,
      })
    }
  }, [isCustomizationEnabled, state.isCustomizationEnabled])

  const customizationId =
    (state.customizeApiData as CustomizationOptions)?.customizationId ??
    // user has never saved customizations
    NEVER_SAVED_CUSTOMIZATIONS

  const { updateCustomization } = useUpdateCustomizationData(customizationId)

  const contextValue: CustomizeDashboardContextValue = useMemo(() => {
    const newUser = customizationId === NEVER_SAVED_CUSTOMIZATIONS
    const method = newUser ? 'POST' : 'PATCH'

    const payload =
      method === 'POST'
        ? mergeDeepRight(state.customizeApiData, state.customizeDataToPatch)
        : state.customizeDataToPatch

    async function saveCustomizations(): Promise<void> {
      await saveCustomizationsUtil(
        customizeDispatch,
        payload,
        updateCustomization,
      )

      // update customizations for the app
      // Ex: customization is also used in the <AccountsDropdown> of the host app
      hostDataCustomization.update()
    }

    const existingViews = state.customizeApiData.views
    function setDashboardView(view: DashboardView): void {
      saveDashboardView(
        customizeDispatch,
        view,
        updateCustomization,
        existingViews,
        method,
        payload,
        hostDataCustomization,
      )
    }

    return [
      {
        ...state,
        eligibleWidgets,
        setDashboardView,
        saveCustomizations,
      },
      customizeDispatch,
    ]
  }, [customizationId, eligibleWidgets, hostDataCustomization, state])

  return (
    <CustomizeDashboardContext.Provider value={contextValue}>
      {children}
    </CustomizeDashboardContext.Provider>
  )
}

export function useCustomizeDashboard(): CustomizeDashboardContextValue {
  const context = useContext(CustomizeDashboardContext)
  if (context === undefined)
    throw new Error(
      'useCustomizeDashboard must be used within a CustomizeDashboardProvider',
    )
  return context
}
